import { Directive, ElementRef, NgZone, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Replaces the browser's dated option list of a plain <select> with a modern dropdown panel.
 *
 * The <select> itself stays in the page (same look when closed, same bindings). When the user
 * picks an option we set it on the real <select> and fire its normal `change` / `input` events,
 * so [(ngModel)], formControlName, (change)="...$event.target.value" etc. keep working as before.
 *
 * Skipped for: touch devices (the native picker is better there), `multiple` / `size` selects,
 * and any select with the `native-select` class.
 */
@Directive({
  selector: 'select:not([multiple]):not([size]):not(.native-select)',
  standalone: true,
})
export class ModernSelectDirective implements OnInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLSelectElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private panel: HTMLDivElement | null = null;
  private activeIndex = -1;
  private enabled = false;
  private cleanup: Array<() => void> = [];

  private get select(): HTMLSelectElement {
    return this.host.nativeElement;
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;
    // Phones/tablets: keep the native picker
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    this.enabled = true;

    const sel = this.select;
    this.listen(sel, 'mousedown', (e: Event) => this.onMouseDown(e as MouseEvent));
    this.listen(sel, 'keydown', (e: Event) => this.onSelectKeyDown(e as KeyboardEvent));
  }

  ngOnDestroy(): void {
    this.close();
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }

  // ---------- Opening ----------

  private onMouseDown(e: MouseEvent): void {
    if (e.button !== 0 || this.select.disabled) return;
    e.preventDefault(); // stops the native list
    this.select.focus();
    this.panel ? this.close() : this.open();
  }

  private onSelectKeyDown(e: KeyboardEvent): void {
    if (this.select.disabled) return;

    if (this.panel) {
      this.onPanelKeyDown(e);
      return;
    }
    const opens = e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp' ||
      (e.altKey && e.key === 'ArrowDown') || e.key === 'F4';
    if (opens) {
      e.preventDefault();
      this.open();
    }
  }

  private open(): void {
    if (!this.enabled || this.panel) return;
    const sel = this.select;
    const options = Array.from(sel.options);
    if (!options.length) return;

    const panel = document.createElement('div');
    panel.className = 'modern-select-panel';
    panel.setAttribute('role', 'listbox');
    panel.tabIndex = -1;

    options.forEach((opt, index) => {
      if (opt.hidden) return;
      const item = document.createElement('div');
      item.className = 'modern-select-option';
      item.setAttribute('role', 'option');
      item.dataset['index'] = String(index);
      item.textContent = opt.textContent?.trim() || ' ';

      const isPlaceholder = opt.value === '' && index === 0;
      if (isPlaceholder) item.classList.add('is-placeholder');
      if (opt.disabled) {
        item.classList.add('is-disabled');
        item.setAttribute('aria-disabled', 'true');
      }
      if (index === sel.selectedIndex) {
        item.classList.add('is-selected');
        item.setAttribute('aria-selected', 'true');
      }

      item.addEventListener('mousedown', ev => ev.preventDefault()); // keep focus on the select
      item.addEventListener('click', () => this.choose(index));
      item.addEventListener('mousemove', () => this.setActive(index, false));
      panel.appendChild(item);
    });

    document.body.appendChild(panel);
    this.panel = panel;
    this.position();
    this.setActive(sel.selectedIndex >= 0 ? sel.selectedIndex : this.firstEnabled(), true);
    sel.classList.add('modern-select-open');
    sel.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => panel.classList.add('is-visible'));

    // Close on outside click, scroll (outside the panel), resize, or focus leaving
    const onDocDown = (ev: Event) => {
      if (!panel.contains(ev.target as Node) && ev.target !== sel) this.close();
    };
    const onScroll = (ev: Event) => {
      if (!panel.contains(ev.target as Node)) this.close();
    };
    const onResize = () => this.close();
    const onBlur = () => setTimeout(() => {
      if (document.activeElement !== sel) this.close();
    });
    document.addEventListener('mousedown', onDocDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    sel.addEventListener('blur', onBlur);
    this.panelCleanup = () => {
      document.removeEventListener('mousedown', onDocDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      sel.removeEventListener('blur', onBlur);
    };
  }

  private panelCleanup: (() => void) | null = null;

  private close(): void {
    if (!this.panel) return;
    this.panelCleanup?.();
    this.panelCleanup = null;
    this.panel.remove();
    this.panel = null;
    this.activeIndex = -1;
    this.select.classList.remove('modern-select-open');
    this.select.setAttribute('aria-expanded', 'false');
  }

  /** Below the select, or above it when there isn't room. Fixed position so table/card overflow can't clip it. */
  private position(): void {
    if (!this.panel) return;
    const rect = this.select.getBoundingClientRect();
    const panel = this.panel;
    const gap = 6;
    const maxHeight = 280;

    panel.style.minWidth = `${Math.max(rect.width, 160)}px`;
    panel.style.left = `${Math.min(rect.left, window.innerWidth - Math.max(rect.width, 160) - 8)}px`;

    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const openUp = spaceBelow < Math.min(maxHeight, panel.scrollHeight) && spaceAbove > spaceBelow;
    const available = Math.max(120, Math.min(maxHeight, openUp ? spaceAbove : spaceBelow));
    panel.style.maxHeight = `${available}px`;

    if (openUp) {
      panel.style.top = '';
      panel.style.bottom = `${window.innerHeight - rect.top + gap}px`;
      panel.classList.add('opens-up');
    } else {
      panel.style.bottom = '';
      panel.style.top = `${rect.bottom + gap}px`;
    }
  }

  // ---------- Choosing ----------

  private choose(index: number): void {
    const sel = this.select;
    const opt = sel.options[index];
    if (!opt || opt.disabled) return;

    const changed = sel.selectedIndex !== index;
    sel.selectedIndex = index;
    this.close();
    sel.focus();
    if (changed) {
      // Same events the browser fires, so Angular bindings and (change) handlers run as usual
      this.zone.run(() => {
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  // ---------- Keyboard inside the open list ----------

  private onPanelKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        e.preventDefault();
        this.setActive(this.firstEnabled(), true);
        break;
      case 'End':
        e.preventDefault();
        this.setActive(this.lastEnabled(), true);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this.activeIndex >= 0) this.choose(this.activeIndex);
        break;
      case 'Escape':
      case 'Tab':
        if (e.key === 'Escape') e.preventDefault();
        this.close();
        break;
      default:
        // Type-ahead: jump to the first option starting with the typed letter
        if (e.key.length === 1 && /\S/.test(e.key)) {
          e.preventDefault();
          this.jumpTo(e.key.toLowerCase());
        }
    }
  }

  private moveActive(step: number): void {
    const opts = this.select.options;
    let i = this.activeIndex;
    for (let n = 0; n < opts.length; n++) {
      i = (i + step + opts.length) % opts.length;
      if (!opts[i].disabled && !opts[i].hidden) {
        this.setActive(i, true);
        return;
      }
    }
  }

  private jumpTo(letter: string): void {
    const opts = Array.from(this.select.options);
    const start = this.activeIndex + 1;
    const order = [...opts.slice(start), ...opts.slice(0, start)];
    const match = order.find(o => !o.disabled && !o.hidden && (o.textContent || '').trim().toLowerCase().startsWith(letter));
    if (match) this.setActive(match.index, true);
  }

  private setActive(index: number, scroll: boolean): void {
    if (!this.panel) return;
    this.activeIndex = index;
    this.panel.querySelectorAll('.modern-select-option').forEach(el => {
      const active = Number((el as HTMLElement).dataset['index']) === index;
      el.classList.toggle('is-active', active);
      if (active && scroll) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    });
  }

  private firstEnabled(): number {
    return Array.from(this.select.options).findIndex(o => !o.disabled && !o.hidden);
  }

  private lastEnabled(): number {
    const opts = Array.from(this.select.options);
    for (let i = opts.length - 1; i >= 0; i--) if (!opts[i].disabled && !opts[i].hidden) return i;
    return -1;
  }

  private listen(el: EventTarget, type: string, fn: (e: Event) => void): void {
    el.addEventListener(type, fn);
    this.cleanup.push(() => el.removeEventListener(type, fn));
  }
}
